import React, { useState, useEffect } from 'react';
import Navbar from '../components/NavBar';
import ClubCard from '../components/ClubCard';
import { getAllClubs, joinClub, leaveClub, getMyClubs } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Target, Heart, MapPin, Navigation, X, ChevronDown, LogIn, Users, UserPlus  } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DotLoader } from 'react-spinners';
import { requestNotificationPermission } from '../services/firebase';
import Footer from '../components/Footer';
import FooterPWA from '../components/FooterPWA';
import PulseLogo from '../assets/PulseLogoMin.png'

function Home() {
  const { user, isAuthenticated } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [myClubs, setMyClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationLoaded, setLocationLoaded] = useState(false);
  const [showLocationBanner, setShowLocationBanner] = useState(true);
  const navigate = useNavigate();

  // Charger la localisation depuis le localStorage au montage du composant
  useEffect(() => {
    const savedLocation = localStorage.getItem('userLocation');
    if (savedLocation) {
      try {
        const location = JSON.parse(savedLocation);
        setUserLocation(location);
        console.log('📍 Localisation récupérée du cache:', location);
      } catch (error) {
        console.error('Erreur lors du chargement de la localisation:', error);
        localStorage.removeItem('userLocation');
      }
    }
    setLocationLoaded(true);
  }, []);

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Charger les clubs SEULEMENT après que la localisation soit chargée
  useEffect(() => {
    if (locationLoaded) {
      fetchClubs();
      if (isAuthenticated) {
        fetchMyClubs();
      }
    }
  }, [isAuthenticated, userLocation, locationLoaded]);

  const requestUserLocation = async () => {
    if (!("geolocation" in navigator)) {
      alert("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }

    setLocationLoading(true);

    const getPosition = () => {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
    };

    try {
      const position = await getPosition();
      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        timestamp: Date.now(),
      };
      setUserLocation(location);
      localStorage.setItem("userLocation", JSON.stringify(location));
      setShowLocationBanner(true);
      console.log("📍 Position utilisateur sauvegardée:", location);
    } catch (error) {
      console.error("❌ Erreur de géolocalisation:", error);
      if (error.code === 1) {
        alert("Veuillez autoriser la géolocalisation dans les réglages du navigateur.");
      } else if (error.code === 2) {
        alert("Position indisponible. Vérifiez votre connexion GPS ou Wi-Fi.");
      } else if (error.code === 3) {
        alert("Délai dépassé. Réessayez.");
      } else {
        alert("Erreur inconnue de géolocalisation.");
      }
    } finally {
      setLocationLoading(false);
    }
  };

  const clearUserLocation = () => {
    setUserLocation(null);
    localStorage.removeItem('userLocation');
    setShowLocationBanner(false);
    console.log('🗑️ Localisation supprimée');
  };

  const fetchClubs = async () => {
    try {
      console.log('🔄 Chargement des clubs avec localisation:', userLocation);
      const data = await getAllClubs(userLocation);
      setClubs(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des clubs:', error);
      setLoading(false);
    }
  };

  const fetchMyClubs = async () => {
    try {
      const data = await getMyClubs();
      setMyClubs(data);
    } catch (error) {
      console.error('Erreur lors du chargement de mes clubs:', error);
    }
  };

  const handleJoinClub = async (clubId) => {
    if (!isAuthenticated) {
      alert('Vous devez être connecté pour rejoindre un club');
      return;
    }

    try {
      await joinClub(clubId);
      fetchClubs();
      fetchMyClubs();
    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'inscription');
    }
  };

  const handleLeaveClub = async (clubId) => {
    try {
      await leaveClub(clubId);
      fetchClubs();
      fetchMyClubs();
    } catch (error) {
      console.error('Erreur lors de la sortie du club:', error);
      alert(error.response?.data?.message || 'Erreur lors de la sortie');
    }
  };

  const isMemberOfClub = (club) => {
    if (!isAuthenticated || !user) return false;
    return club.membres?.some(membre => membre === user._id || membre._id === user._id);
  };

  const getLocationAge = () => {
    if (!userLocation || !userLocation.timestamp) return null;
    const ageInHours = (Date.now() - userLocation.timestamp) / (1000 * 60 * 60);
    return Math.round(ageInHours * 10) / 10;
  };

  const locationAge = getLocationAge();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  const displayedClubs = activeTab === 'myClubs' ? myClubs : clubs;

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Hero Section */}
        <div className="mb-16">
          <div className="text-center max-w-4xl mx-auto space-y-6">
            <div className="flex flex-col items-center pt-20">
              <div className="relative">
                {/* Double glow pour plus d'intensité */}
                <div className="absolute inset-0 bg-indigo-500/40 blur-3xl rounded-full animate-pulse"></div>
                <div className="absolute inset-0 bg-purple-500/30 blur-2xl rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                
                {/* Logo */}
                <img 
                  src={PulseLogo} 
                  alt="Run Clubs" 
                  className="relative w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 object-contain drop-shadow-[0_0_30px_rgba(99,102,241,0.5)]"
                />
              </div>
            </div>
            <p className="text-xl sm:text-2xl md:text-3xl text-gray-400 font-light px-4 mt-6 text-center">
              Trouve ton groupe de course
            </p>
                        
            {/* CTA */}
            <div className="pt-4">
              {!isAuthenticated && (
                // Non connecté
                <div className="space-y-4">
                  <button
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center space-x-2 px-8 sm:px-10 py-4 sm:py-5 bg-indigo-500 hover:bg-indigo-600 text-white rounded-2xl font-medium transition-all text-base sm:text-lg shadow-xl shadow-indigo-500/20 hover:shadow-2xl hover:shadow-indigo-500/30 transform hover:scale-[1.02]"
                  >
                    <span>Se connecter</span>
                    <LogIn size={20} />
                  </button>
                  <p className="text-sm text-gray-400">
                    Pas encore inscrit ?{' '}
                    <button
                      onClick={() => navigate('/register')}
                      className="text-indigo-500 hover:text-indigo-400 font-semibold underline"
                    >
                      Créer un compte
                    </button>
                  </p>
                </div>
              )}
              {/* Si localisation active, on n'affiche rien */}
            </div>

            
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 mt-16 mb-12">
  

  <button
    onClick={() => navigate('/clubs-list')}
    className="flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 
               bg-indigo-500 text-white rounded-xl 
               transition-all duration-300 shadow-lg shadow-indigo-500/30 
               transform hover:scale-[1.05] active:scale-[0.98]
               pulse-glow"
    style={{ animationDelay: '0.5s' }}
  >
    <Users size={20} />
    <span>Voir les groupes</span>
  </button>
  <button
    onClick={() => navigate('/find-friends')}
    className="flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 
               bg-indigo-500 text-white rounded-xl 
               transition-all duration-300 shadow-lg shadow-indigo-500/30 
               transform hover:scale-[1.05] active:scale-[0.98]
               pulse-glow"
  >
    <UserPlus size={20} />
    <span>Voir les amis</span>
  </button>
</div>



        

        



      </main>
      <FooterPWA />
    </div>
  );
}

export default Home;